import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { getReviewsRating } from '../../../api/review';
import ReviewDetail from '../../../Member/review/components/ReviewDetail';
import type { Diary } from '../../../type/recipe';
import type { RenewalReview } from '../../../type/review';
import { useAlertModal, useGlobalModal } from '../hook';

export const useReviewRating = (
  productId: number | undefined,
  moGraphLength: number,
) => {
  const [ratingCount, setRatingCount] = useState<{ [key: number]: number }>({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const getTotalReviewCount = () => {
    return Object.values(ratingCount).reduce((a, b) => a + b, 0);
  };

  const getRatingAverage = () => {
    let sum = 0;
    for (const key in ratingCount) {
      sum += ratingCount[Number(key)] * Number(key);
    }
    if (getTotalReviewCount() === 0) {
      return 0;
    } else {
      return +(sum / getTotalReviewCount()).toFixed(1);
    }
  };

  const parsedRating = Object.entries(ratingCount)
    .reverse()
    .map((arr) => {
      const [point, count] = arr;
      return {
        percentage: Math.floor(
          (getTotalReviewCount() ? count / getTotalReviewCount() : 0) * 100,
        ),
        graphBarLength:
          (getTotalReviewCount() ? count / getTotalReviewCount() : 0) *
          moGraphLength,
        point,
      };
    });

  const totalPercentage = parsedRating.reduce(
    (prev, curr) => prev + curr.percentage,
    0,
  );

  const calculatedRating = (() => {
    if (totalPercentage === 100 || totalPercentage === 0) {
      return parsedRating;
    }

    // 5점 항목에 나머지 점수를 추가해준다
    parsedRating[0].percentage += 100 - totalPercentage;
    return parsedRating;
  })();

  const fetchReviewsRating = useCallback(async () => {
    try {
      setRatingCount((await getReviewsRating(productId)).data);
    } catch (error) {
      console.log(error);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviewsRating();
  }, [fetchReviewsRating]);

  return { calculatedRating, getTotalReviewCount, getRatingAverage };
};

export const useCopyCurrentLink = () => {
  const { openAlertModal } = useAlertModal();

  const handleClickShareLink = async (path?: string) => {
    if (typeof window === 'undefined') return;

    const url = window?.location.href;
    try {
      await navigator.clipboard.writeText(path ? url + path : url);
      openAlertModal({ content: '링크가 복사되었습니다.' });
    } catch (err) {
      openAlertModal({
        content: '링크 복사에 실패했습니다. 다시 시도해주세요.',
      });
    }
  };

  return { handleClickShareLink };
};

export const useGlobalReviewModal = () => {
  const { openGlobalModal, closeGlobalModal } = useGlobalModal();

  const openGlobalReviewModal = (
    review: RenewalReview | Diary,
    children: React.ReactNode | React.ReactElement,
  ) => {
    openGlobalModal({
      modalContent: (
        <ReviewDetail review={review} onClose={() => closeGlobalModal()}>
          {children}
        </ReviewDetail>
      ),
      modalContentStyle: { top: 0, transform: 'translateX(-50%)' },
    });
  };

  const closeGlobalReviewModal = () => {
    closeGlobalModal();
  };

  return { openGlobalReviewModal, closeGlobalReviewModal };
};
